import { createSignal, createMemo, createResource, onMount, For, Show, Index } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemStackView from '../../components/ItemStackView';
import { fromItemStackDto } from '../../services/itemRefAdapters';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import { RelativeTime } from '../../components/RelativeTime';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import OrbsTraderNav from '../../components/OrbsTraderNav';
import { formatDateTime } from '../../services/dateFormat';
import { firstError } from '../../api/envelope';
import type { OrbShopProductDto, OrbsTraderConfigDto, OrbsTraderLine } from '../../api/generated/schemas';
import styles from './Demolish.module.css';
import orbStyles from './OrbsTrader.module.css';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];
const CATEGORIES = [0, 1, 2];
const OUTCOME_COMPLETED = 1;
const OUTCOME_PRICE_CHANGED = 3;

const formatOrbCount = (n: number) => new Intl.NumberFormat('de-DE').format(n);

export default function OrbsTraderPage() {
    return <BotScoped groupAware><OrbsTraderInner /></BotScoped>;
}

function OrbsTraderInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const inv = useInventoryApi();

    const [configs, setConfigs] = createSignal<OrbsTraderConfigDto[]>([]);
    const [catalog, setCatalog] = createSignal<OrbShopProductDto[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal<number | null>(null);
    const [formLines, setFormLines] = createSignal<OrbsTraderLine[]>([]);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';

    const [balance] = createResource(botId, async (id) => {
        if (!id) return null;
        const res = await api.profileInfo.getLatest(id);
        return isOk(res.data) ? res.data.value : null;
    });

    const productsByKey = createMemo(() => new Map(catalog().map(p => [p.key, p])));
    const linesCost = (lines: OrbsTraderLine[]) =>
        lines.reduce((sum, line) => sum + (productsByKey().get(line.productKey)?.price ?? 0) * line.count, 0);

    onMount(async () => {
        try {
            const [cfgRes, catalogRes, storageList] = await Promise.all([
                api.orbsTrader.getOrbsTraderConfigs(),
                api.orbsTrader.getOrbShopCatalog(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs(cfgRes.data.value.configs ?? []);
            if (isOk(catalogRes.data)) setCatalog(catalogRes.data.value.products ?? []);
            else toast?.error(firstError(catalogRes.data) ?? t('ORBS_TRADER_CATALOG_FAILED'));
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('ORBS_TRADER_CATALOG_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.orbsTrader.getOrbsTraderConfigs();
        if (isOk(res.data)) setConfigs(res.data.value.configs ?? []);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormStorageId(storages()[0]?.id ?? '');
        setFormCitybuild(null);
        setFormLines([{ productKey: catalog()[0]?.key ?? '', count: 1 }]);
        setShowForm(true);
    }

    function openEdit(c: OrbsTraderConfigDto) {
        setEditId(c.id);
        setFormName(c.name);
        setFormStorageId(c.storageId);
        setFormCitybuild(c.citybuild ?? null);
        setFormLines(c.lines.map(line => ({ ...line })));
        setShowForm(true);
    }

    function updateLine(index: number, patch: Partial<OrbsTraderLine>) {
        setFormLines(lines => lines.map((line, i) => i === index ? { ...line, ...patch } : line));
    }

    function moveLine(index: number, offset: number) {
        setFormLines(lines => {
            const target = index + offset;
            if (target < 0 || target >= lines.length) return lines;
            const reordered = [...lines];
            [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
            return reordered;
        });
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('ORBS_TRADER_SELECT_STORAGE')); return; }
        if (formLines().length === 0) { toast?.error(t('ORBS_TRADER_NO_ITEMS')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || t('ORBS_TRADER_TITLE'),
            storageId: formStorageId(),
            citybuild: formCitybuild(),
            lines: formLines(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.orbsTrader.updateOrbsTraderConfig(id, body)
                : await api.orbsTrader.createOrbsTraderConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('ORBS_TRADER_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('ORBS_TRADER_UPDATED') : t('ORBS_TRADER_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('ORBS_TRADER_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.orbsTrader.deleteOrbsTraderConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('ORBS_TRADER_DELETE_FAILED'));
            return;
        }
        toast?.info(t('ORBS_TRADER_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('ORBS_TRADER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.orbsTrader.runOrbsTraderConfig(id, { botId: targetBotId }),
            { success: t('ORBS_TRADER_STARTED'), failure: t('ORBS_TRADER_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('ORBS_TRADER_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.orbsTrader.stopOrbsTrader(id, { botId: targetBotId }),
            { success: t('ORBS_TRADER_STOPPED'), failure: t('ORBS_TRADER_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8);
    }

    function citybuildLabel(c: OrbsTraderConfigDto) {
        return c.citybuild != null ? Citybuild[c.citybuild] : t('ORBS_TRADER_CITYBUILD_FROM_STORAGE');
    }

    function productIcon(product: OrbShopProductDto | undefined) {
        if (!product) return null;
        return fromItemStackDto({ key: product.key, itemId: product.itemId, metadata: product.damage, count: 1, isCustom: false });
    }

    function outcomeLabel(outcome: number, livePrice?: number | null) {
        if (outcome === OUTCOME_PRICE_CHANGED && livePrice != null)
            return t('ORBS_TRADER_OUTCOME_3', formatOrbCount(livePrice));
        return t(`ORBS_TRADER_OUTCOME_${outcome}`);
    }

    function CostSummary(props: { lines: OrbsTraderLine[] }) {
        const total = () => linesCost(props.lines);
        const orbs = () => balance()?.orbs ?? null;
        const remaining = () => orbs() != null ? orbs()! - total() : null;
        return (
            <div class={orbStyles.summary}>
                <div class={orbStyles.summaryRow}>
                    <span><Text val="ORBS_TRADER_TOTAL" /></span>
                    <span class={orbStyles.amount}>{t('ORBS_TRADER_ORBS', formatOrbCount(total()))}</span>
                </div>
                <div class={orbStyles.summaryRow}>
                    <span><Text val="ORBS_TRADER_BALANCE" /></span>
                    <Show when={botId()} fallback={<span class={orbStyles.muted}><Text val="ORBS_TRADER_BALANCE_NO_BOT" /></span>}>
                        <Show when={orbs() != null} fallback={<span class={orbStyles.muted}><Text val="ORBS_TRADER_BALANCE_UNKNOWN" /></span>}>
                            <span class={orbStyles.amount}>
                                {t('ORBS_TRADER_ORBS', formatOrbCount(orbs()!))}
                                <span class={orbStyles.muted}> · <RelativeTime value={balance()?.orbsObservedAt} /></span>
                            </span>
                        </Show>
                    </Show>
                </div>
                <Show when={remaining() != null}>
                    <div class={`${orbStyles.summaryRow} ${remaining()! < 0 ? orbStyles.short : ''}`}>
                        <span><Text val={remaining()! < 0 ? 'ORBS_TRADER_SHORT_LABEL' : 'ORBS_TRADER_REMAINING'} /></span>
                        <span class={orbStyles.amount}>{t('ORBS_TRADER_ORBS', formatOrbCount(Math.abs(remaining()!)))}</span>
                    </div>
                </Show>
            </div>
        );
    }

    return (
        <div class={styles.container}>
            <OrbsTraderNav />
            <div class={styles.headerRow}>
                <h1><Text val="ORBS_TRADER_TITLE" /></h1>
                <button class="btn btn-primary" onClick={openCreate} disabled={loading() || catalog().length === 0}>
                    <i class="fa-solid fa-plus" /> <Text val="ORBS_TRADER_NEW_CONFIG" />
                </button>
            </div>
            <p class={styles.subtitle}><Text val="ORBS_TRADER_SUBTITLE" /></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="ORBS_TRADER_EDIT" /> : <Text val="ORBS_TRADER_CREATE" />}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_TRADER_F_NAME" /></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('ORBS_TRADER_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_TRADER_F_STORAGE" /></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('ORBS_TRADER_SELECT_STORAGE_OPTION')}
                                />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="ORBS_TRADER_F_CITYBUILD" /></span>
                                <select
                                    class={styles.input}
                                    value={formCitybuild() ?? ''}
                                    onChange={e => setFormCitybuild(e.currentTarget.value === '' ? null : parseInt(e.currentTarget.value))}
                                >
                                    <option value="">{t('ORBS_TRADER_CITYBUILD_FROM_STORAGE')}</option>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>
                        </div>

                        <div class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="ORBS_TRADER_F_ITEMS" /></span>
                            <div class={orbStyles.lines}>
                                <Index each={formLines()}>
                                    {(line, index) => {
                                        const product = () => productsByKey().get(line().productKey);
                                        return (
                                            <div class={orbStyles.lineRow}>
                                                <div class={orbStyles.lineIcon}>
                                                    <Show when={productIcon(product())}>
                                                        {(stack) => <ItemStackView stack={stack()} size={24} bare />}
                                                    </Show>
                                                </div>
                                                <select
                                                    class={`${styles.input} ${orbStyles.lineProduct}`}
                                                    value={line().productKey}
                                                    onChange={e => updateLine(index, { productKey: e.currentTarget.value })}
                                                >
                                                    <For each={CATEGORIES}>
                                                        {(category) => (
                                                            <optgroup label={t(`ORBS_TRADER_CATEGORY_${category}`)}>
                                                                <For each={catalog().filter(p => p.category === category)}>
                                                                    {(p) => <option value={p.key}>{p.label} ({t('ORBS_TRADER_ORBS', formatOrbCount(p.price))})</option>}
                                                                </For>
                                                            </optgroup>
                                                        )}
                                                    </For>
                                                </select>
                                                <input
                                                    class={`${styles.input} ${orbStyles.lineCount}`}
                                                    type="number"
                                                    min="1"
                                                    value={line().count}
                                                    onInput={e => updateLine(index, { count: Math.max(1, parseInt(e.currentTarget.value) || 1) })}
                                                />
                                                <span class={`${orbStyles.amount} ${orbStyles.lineCost}`}>
                                                    {t('ORBS_TRADER_ORBS', formatOrbCount((product()?.price ?? 0) * line().count))}
                                                </span>
                                                <div class={orbStyles.lineActions}>
                                                    <button type="button" class="btn btn-secondary btn-sm btn-icon" title={t('ORBS_TRADER_MOVE_UP')} disabled={index === 0} onClick={() => moveLine(index, -1)}>
                                                        <i class="fa-solid fa-arrow-up" />
                                                    </button>
                                                    <button type="button" class="btn btn-secondary btn-sm btn-icon" title={t('ORBS_TRADER_MOVE_DOWN')} disabled={index === formLines().length - 1} onClick={() => moveLine(index, 1)}>
                                                        <i class="fa-solid fa-arrow-down" />
                                                    </button>
                                                    <button type="button" class="btn btn-danger btn-sm btn-icon" title={t('ORBS_TRADER_REMOVE_ITEM')} onClick={() => setFormLines(lines => lines.filter((_, i) => i !== index))}>
                                                        <i class="fa-solid fa-trash" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Index>
                            </div>
                            <div>
                                <button type="button" class="btn btn-secondary btn-sm" onClick={() => setFormLines(lines => [...lines, { productKey: catalog()[0]?.key ?? '', count: 1 }])}>
                                    <i class="fa-solid fa-plus" /> <Text val="ORBS_TRADER_ADD_ITEM" />
                                </button>
                            </div>
                        </div>

                        <CostSummary lines={formLines()} />

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId() || formLines().length === 0}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE" />
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-cart-shopping" />
                        <p><Text val="ORBS_TRADER_EMPTY" /></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>{storageName(c.storageId)} · {citybuildLabel(c)}</div>
                                    </div>
                                </div>

                                <div class={styles.cardBody}>
                                    <For each={c.lines}>
                                        {(line) => {
                                            const product = productsByKey().get(line.productKey);
                                            return (
                                                <div class={styles.infoRow}>
                                                    <Show when={productIcon(product)} fallback={<i class="fa-solid fa-circle-question" />}>
                                                        {(stack) => <ItemStackView stack={stack()} size={20} bare />}
                                                    </Show>
                                                    <span>{line.count} × {product?.label ?? line.productKey}</span>
                                                    <span class={`${orbStyles.amount} ${orbStyles.push}`}>
                                                        {t('ORBS_TRADER_ORBS', formatOrbCount((product?.price ?? 0) * line.count))}
                                                    </span>
                                                </div>
                                            );
                                        }}
                                    </For>
                                </div>

                                <CostSummary lines={c.lines} />

                                <Show when={c.lastRun}>
                                    {(run) => (
                                        <div class={orbStyles.lastRun}>
                                            <div class={orbStyles.lastRunHeader}>
                                                <Text val="ORBS_TRADER_LAST_RUN" />
                                                <span class={orbStyles.muted}>
                                                    {formatDateTime(run().startedAt)}
                                                    {run().finishedAt ? '' : ` · ${t('ORBS_TRADER_LAST_RUN_RUNNING')}`}
                                                </span>
                                            </div>
                                            <For each={run().lines}>
                                                {(result) => (
                                                    <div class={orbStyles.summaryRow}>
                                                        <span>{productsByKey().get(result.productKey)?.label ?? result.productKey} {result.bought}/{result.requested}</span>
                                                        <span class={result.outcome === OUTCOME_COMPLETED ? orbStyles.muted : orbStyles.short}>
                                                            {outcomeLabel(result.outcome, result.livePrice)}
                                                        </span>
                                                    </div>
                                                )}
                                            </For>
                                            <div class={orbStyles.muted}>{t('ORBS_TRADER_LAST_RUN_SPENT', formatOrbCount(run().orbsSpent))}</div>
                                            <Show when={run().depositFailed}>
                                                <div class={orbStyles.short}><Text val="ORBS_TRADER_LAST_RUN_DEPOSIT_FAILED" /></div>
                                            </Show>
                                        </div>
                                    )}
                                </Show>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="ORBS_TRADER_RUN" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
